import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'

import PeriodPicker from 'pages/parts/PeriodPicker'
import { formatDate, formatMoney, returnSign } from 'helpers/helpers'
import { httpOk, GET } from 'helpers/api'

const cardStyle = {
	border: '1px solid var(--dark-border, #edf0f4)',
	borderRadius: '12px',
	boxShadow: '0 4px 14px rgba(15, 23, 42, 0.04)',
}

const mutedStyle = {
	color: 'var(--dark-text-muted, #8791a1)',
	fontSize: '12px',
}

const moneyCellStyle = {
	fontWeight: 600,
	color: 'var(--dark-text-strong, #111827)',
	whiteSpace: 'nowrap',
}

function flattenTree(nodes, acc = []) {
	nodes?.forEach((node) => {
		acc.push(node)
		if (node.children?.length) {
			flattenTree(node.children, acc)
		}
	})
	return acc
}

// Красим долг: у кого он больше остальных — красным. Партнёров без долга
// не подсвечиваем вовсе, иначе треть таблицы всегда была бы красной.
function buildDebtColorMap(tree) {
	const items = flattenTree(tree).filter((item) => Number(item.debt_amount || 0) > 0)
	if (items.length === 0) {
		return {}
	}

	const sorted = [...items].sort((left, right) => (
		Number(right.debt_amount || 0) - Number(left.debt_amount || 0)
	))
	const redLimit = Math.ceil(sorted.length / 3)
	const yellowLimit = Math.ceil((sorted.length * 2) / 3)
	const map = {}

	sorted.forEach((item, index) => {
		if (index < redLimit) {
			map[item.id] = 'red'
			return
		}
		if (index < yellowLimit) {
			map[item.id] = 'yellow'
			return
		}
		map[item.id] = 'green'
	})

	return map
}

const badgeStyle = {
	borderRadius: '8px',
	padding: '3px 10px',
	fontWeight: 600,
	fontSize: '12px',
	display: 'inline-block',
	minWidth: '58px',
}

function getDebtBadgeStyle(tier) {
	if (tier === 'red') {
		return {
			background: 'var(--dark-tint-danger, #fdecec)',
			color: 'var(--dark-fg-danger, #d23434)',
		}
	}
	if (tier === 'yellow') {
		return {
			background: 'var(--dark-tint-warning, #fff7df)',
			color: 'var(--dark-fg-warning, #b7791f)',
		}
	}
	return {
		background: 'var(--dark-tint-success, #ecfdf3)',
		color: 'var(--dark-fg-success, #1f9d57)',
	}
}

function PartnerTreeTable({ data, level = 0, formatMoneyFn, formatPercentFn, debtColorMap }) {
	return (
		<>
			{data?.map((item) => (
				<Fragment key={item.id}>
					<tr>
						<td style={{ paddingLeft: `${level * 18 + 12}px` }}>
							<div style={{ fontWeight: 600, color: 'var(--dark-text-strong, #111827)' }}>
								{level > 0 && <span style={{ color: '#cbd5e1', marginRight: '6px' }}>└</span>}
								#{item.id} {item.first_name || item.login}
							</div>
							<div style={mutedStyle}>{item.login}</div>
						</td>
						<td className="text-end" style={moneyCellStyle}>{formatMoneyFn(item.charged_amount || 0)}</td>
						<td className="text-end" style={moneyCellStyle}>{formatMoneyFn(item.topup_amount || 0)}</td>
						<td className="text-end" style={moneyCellStyle}>{formatMoneyFn(item.allocated_amount || 0)}</td>
						<td className="text-end">
							<span style={{ ...badgeStyle, ...getDebtBadgeStyle(debtColorMap[item.id]) }}>
								{formatMoneyFn(item.debt_amount || 0)}
							</span>
						</td>
						<td className="text-end" style={{ ...moneyCellStyle, color: 'var(--dark-fg-success, #1f9d57)' }}>{formatMoneyFn(item.bonus_amount || 0)}</td>
						<td className="text-end">
							<span style={{ ...badgeStyle, background: 'var(--dark-tint-primary, #eef2ff)', color: '#4c8df0' }}>
								{formatPercentFn(item.rate_percent || 0)}
							</span>
						</td>
						<td className="text-end" style={moneyCellStyle}>{formatMoneyFn(item.potential_bonus_amount || 0)}</td>
						<td className="text-end" style={moneyCellStyle}>{formatMoneyFn(item.paid_bonus_amount || 0)}</td>
					</tr>
					{!!item.children?.length && (
						<PartnerTreeTable
							data={item.children}
							level={level + 1}
							formatMoneyFn={formatMoneyFn}
							formatPercentFn={formatPercentFn}
							debtColorMap={debtColorMap}
						/>
					)}
				</Fragment>
			))}
		</>
	)
}

function PartnerBonusAccrualView({
	accrualApiPath,
	partnersHelperPath = null,
	titleKey = 'admin_partner_bonus_accrual',
	hintKey = 'admin_partner_bonus_accrual_hint',
	partnerFilterPlaceholderKey = 'admin_partner_bonus_accrual_all_partners',
	showBusinessProfitComparison = true,
}) {
	const { t } = useTranslation()
	const today = new Date()
	const showPartnerFilter = !!partnersHelperPath

	const [partnerOptions, setPartnerOptions] = useState([])
	const [filterData, setFilterData] = useState({
		start_date: formatDate(new Date(today.getFullYear(), today.getMonth(), 1), 'yyyy-MM-dd'),
		end_date: formatDate(today, 'yyyy-MM-dd'),
	})
	const [selectedPartners, setSelectedPartners] = useState(null)
	const [tree, setTree] = useState([])
	const [summary, setSummary] = useState({
		total_subscription_amount: 0,
		total_charged_amount: 0,
		total_topup_amount: 0,
		total_allocated_amount: 0,
		total_debt_amount: 0,
		total_potential_bonus_amount: 0,
		total_rate_percent: 0,
		total_bonus_amount: 0,
		total_paid_bonus_amount: 0,
		total_all_bonus_amount: 0,
	})
	const [comparison, setComparison] = useState({
		subscription_with_agents: 0,
		subscription_without_agents: 0,
		subscription_total: 0,
		bonus_earned: 0,
		bonus_paid: 0,
		net_profit_with_agents: 0,
		net_profit_without_agents: 0,
		agent_cost_percent: 0,
	})
	const [loaded, setLoaded] = useState(false)

	const partnerSelectOptions = useMemo(() => (
		partnerOptions.map((item) => ({
			value: item.id,
			label: `#${item.id} ${item.name || item.login} (${item.login})`,
			item,
		}))
	), [partnerOptions])

	const debtColorMap = useMemo(() => buildDebtColorMap(tree), [tree])

	function formatPercent(value) {
		return `${Number(value || 0).toLocaleString('ru-RU', {
			minimumFractionDigits: 0,
			maximumFractionDigits: 2,
		})}%`
	}

	async function loadPartnerOptions() {
		if (!partnersHelperPath) return

		const response = await GET(partnersHelperPath, {}, { loader: false })
		if (httpOk(response)) {
			setPartnerOptions(response.data || [])
		}
	}

	async function loadAccrual(nextFilterData = filterData, nextPartners = selectedPartners) {
		let url = `${accrualApiPath}?start_date=${nextFilterData.start_date}&end_date=${nextFilterData.end_date}`

		if (nextPartners?.length) {
			const partnerIds = nextPartners.map((item) => item.value).join(',')
			url += returnSign(url) + `partner_ids=${partnerIds}`
		}

		const response = await GET(url, {}, { loader: true })
		if (httpOk(response)) {
			setTree(response.data?.tree || [])
			setSummary(response.data?.summary || {
				total_subscription_amount: 0,
				total_charged_amount: 0,
				total_topup_amount: 0,
				total_allocated_amount: 0,
				total_debt_amount: 0,
				total_potential_bonus_amount: 0,
				total_rate_percent: 0,
				total_bonus_amount: 0,
				total_paid_bonus_amount: 0,
				total_all_bonus_amount: 0,
			})
			setComparison(response.data?.comparison || {
				subscription_with_agents: 0,
				subscription_without_agents: 0,
				subscription_total: 0,
				bonus_earned: 0,
				bonus_paid: 0,
				net_profit_with_agents: 0,
				net_profit_without_agents: 0,
				agent_cost_percent: 0,
			})
			setLoaded(true)
		}
	}

	function handleApply() {
		loadAccrual()
	}

	useEffect(() => {
		loadPartnerOptions()
		loadAccrual({
			start_date: formatDate(new Date(today.getFullYear(), today.getMonth(), 1), 'yyyy-MM-dd'),
			end_date: formatDate(today, 'yyyy-MM-dd'),
		}, null)
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accrualApiPath, partnersHelperPath])

	return (
		<div className="pb-3">
			<div className="page-header">
				<h4>{t(titleKey)}</h4>
			</div>

			<div className="card mb-3" style={cardStyle}>
				<div className="card-body">
					<div className="row g-3 align-items-end">
						<div className={showPartnerFilter ? 'col-md-4' : 'col-md-6'}>
							<label className="form-label">{t('period')}</label>
							<PeriodPicker
								startDate={filterData.start_date}
								endDate={filterData.end_date}
								onChange={(period) => setFilterData({ ...filterData, ...period })}
								withTime={false}
								maxDate={today}
							/>
						</div>
						{showPartnerFilter &&
							<div className="col-md-5">
								<label className="form-label">{t('select_partner')}</label>
								<Select
									isMulti
									isClearable
									options={partnerSelectOptions}
									value={selectedPartners}
									onChange={(options) => setSelectedPartners(options?.length ? options : null)}
									placeholder={t(partnerFilterPlaceholderKey)}
								/>
							</div>
						}
						<div className={showPartnerFilter ? 'col-md-3' : 'col-md-6'}>
							<button type="button" className="btn btn-primary w-100" onClick={handleApply}>
								{t('apply_action')}
							</button>
						</div>
					</div>
					<div className="text-muted small mt-2" style={mutedStyle}>
						{t(hintKey)}
					</div>
				</div>
			</div>

			<div className="card" style={cardStyle}>
				<div className="card-body">
					<div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
						<div>
							<div style={{ fontWeight: 700, color: 'var(--dark-text-strong, #111827)' }}>{t(titleKey)}</div>
							<div style={mutedStyle}>
								{filterData.start_date && filterData.end_date
									? `${formatDate(filterData.start_date, 'dd.MM.yyyy')} — ${formatDate(filterData.end_date, 'dd.MM.yyyy')}`
									: t('period')}
							</div>
						</div>
						{loaded && tree.length > 0 &&
							<div className="d-flex flex-wrap gap-3">
								<div style={mutedStyle}>
									{t('admin_partner_bonus_accrual_charged')}:{' '}
									<span style={{ fontWeight: 700, color: 'var(--dark-text-strong, #111827)' }}>
										{formatMoney(summary.total_charged_amount || 0)}
									</span>
								</div>
								<div style={mutedStyle}>
									{t('admin_partner_bonus_accrual_allocated')}:{' '}
									<span style={{ fontWeight: 700, color: 'var(--dark-text-strong, #111827)' }}>
										{formatMoney(summary.total_allocated_amount || 0)}
									</span>
								</div>
								<div style={mutedStyle}>
									{t('admin_partner_bonus_accrual_debt')}:{' '}
									<span style={{ fontWeight: 700, color: 'var(--dark-fg-danger, #d23434)' }}>
										{formatMoney(summary.total_debt_amount || 0)}
									</span>
								</div>
								<div style={mutedStyle}>
									{t('admin_partner_bonus_accrual_bonus')}:{' '}
									<span style={{ fontWeight: 700, color: 'var(--dark-fg-success, #1f9d57)' }}>
										{formatMoney(summary.total_all_bonus_amount || 0)}
									</span>
								</div>
							</div>
						}
					</div>

					<div className="table-responsive">
						<table className="table table-hover align-middle mb-0" style={{ fontSize: '13px' }}>
							<thead>
								<tr style={{ border: 'none' }}>
									<th className="py-3 text-muted fw-semibold" style={{ border: 'none', boxShadow: 'none', background: 'var(--dark-surface, #fff)', fontSize: '13px' }}>{t('partner')}</th>
									<th className="py-3 text-muted fw-semibold text-end" title={t('admin_partner_bonus_accrual_charged_hint')} style={{ border: 'none', boxShadow: 'none', background: 'var(--dark-surface, #fff)', fontSize: '13px', cursor: 'help' }}>{t('admin_partner_bonus_accrual_charged')}</th>
									<th className="py-3 text-muted fw-semibold text-end" title={t('admin_partner_bonus_accrual_topup_hint')} style={{ border: 'none', boxShadow: 'none', background: 'var(--dark-surface, #fff)', fontSize: '13px', cursor: 'help' }}>{t('admin_partner_bonus_accrual_topup')}</th>
									<th className="py-3 text-muted fw-semibold text-end" title={t('admin_partner_bonus_accrual_allocated_hint')} style={{ border: 'none', boxShadow: 'none', background: 'var(--dark-surface, #fff)', fontSize: '13px', cursor: 'help' }}>{t('admin_partner_bonus_accrual_allocated')}</th>
									<th className="py-3 text-muted fw-semibold text-end" style={{ border: 'none', boxShadow: 'none', background: 'var(--dark-surface, #fff)', fontSize: '13px' }}>{t('admin_partner_bonus_accrual_debt')}</th>
									<th className="py-3 text-muted fw-semibold text-end" style={{ border: 'none', boxShadow: 'none', background: 'var(--dark-surface, #fff)', fontSize: '13px' }}>{t('admin_partner_bonus_accrual_bonus')}</th>
									<th className="py-3 text-muted fw-semibold text-end" title={t('admin_partner_bonus_accrual_rate_hint')} style={{ border: 'none', boxShadow: 'none', background: 'var(--dark-surface, #fff)', fontSize: '13px', cursor: 'help' }}>{t('admin_partner_bonus_accrual_rate')}</th>
									<th className="py-3 text-muted fw-semibold text-end" title={t('admin_partner_bonus_accrual_potential_hint')} style={{ border: 'none', boxShadow: 'none', background: 'var(--dark-surface, #fff)', fontSize: '13px', cursor: 'help' }}>{t('admin_partner_bonus_accrual_potential')}</th>
									<th className="py-3 text-muted fw-semibold text-end" style={{ border: 'none', boxShadow: 'none', background: 'var(--dark-surface, #fff)', fontSize: '13px' }}>{t('admin_partner_bonus_accrual_paid')}</th>
								</tr>
							</thead>
							<tbody>
								{loaded && tree.length === 0 &&
									<tr>
										<td colSpan={9} className="text-center text-muted py-4">
											{t('no_data')}
										</td>
									</tr>
								}
								<PartnerTreeTable
									data={tree}
									formatMoneyFn={formatMoney}
									formatPercentFn={formatPercent}
									debtColorMap={debtColorMap}
								/>
							</tbody>
							{loaded && tree.length > 0 &&
						<tfoot>
									<tr style={{ background: 'var(--dark-surface-2, #f8fafc)', borderTop: '2px solid var(--dark-border, #edf0f4)' }}>
										<td style={{ fontWeight: 700, color: 'var(--dark-text-strong, #111827)' }}>{t('total')}</td>
										<td className="text-end" style={{ ...moneyCellStyle, fontWeight: 700 }}>
											{formatMoney(summary.total_charged_amount || 0)}
										</td>
										<td className="text-end" style={{ ...moneyCellStyle, fontWeight: 700 }}>
											{formatMoney(summary.total_topup_amount || 0)}
										</td>
										<td className="text-end" style={{ ...moneyCellStyle, fontWeight: 700 }}>
											{formatMoney(summary.total_allocated_amount || 0)}
										</td>
										<td className="text-end">
											<span style={{ ...badgeStyle, background: 'var(--dark-tint-danger, #fdecec)', color: 'var(--dark-fg-danger, #d23434)', fontWeight: 700 }}>
												{formatMoney(summary.total_debt_amount || 0)}
											</span>
										</td>
										<td className="text-end" style={{ ...moneyCellStyle, fontWeight: 700, color: 'var(--dark-fg-success, #1f9d57)' }}>
											{formatMoney(summary.total_bonus_amount || 0)}
										</td>
										<td className="text-end">
											<span style={{ ...badgeStyle, background: 'var(--dark-tint-primary, #eef2ff)', color: '#4c8df0', fontWeight: 700 }}>
												{formatPercent(summary.total_rate_percent || 0)}
											</span>
										</td>
										<td className="text-end" style={{ ...moneyCellStyle, fontWeight: 700 }}>
											{formatMoney(summary.total_potential_bonus_amount || 0)}
										</td>
										<td className="text-end" style={{ ...moneyCellStyle, fontWeight: 700 }}>
											{formatMoney(summary.total_paid_bonus_amount || 0)}
										</td>
									</tr>
								</tfoot>
							}
						</table>
					</div>
				</div>
			</div>

			{showBusinessProfitComparison && loaded && tree.length > 0 &&
				<div className="card mb-4" style={cardStyle}>
					<div className="card-body">
						<div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
							<div>
								<div style={{ fontWeight: 700, color: 'var(--dark-text-strong, #111827)' }}>
									{t('admin_partner_bonus_accrual_profit_compare_title')}
								</div>
								<div style={mutedStyle}>
									{t('admin_partner_bonus_accrual_profit_compare_hint')}
								</div>
							</div>
							<div style={mutedStyle}>
								{t('admin_partner_bonus_accrual_agent_cost_percent')}:{' '}
								<span style={{ fontWeight: 700, color: 'var(--dark-fg-danger, #d23434)' }}>
									{formatPercent(comparison.agent_cost_percent || 0)}
								</span>
							</div>
						</div>

						<div className="row g-3 mb-3">
							<div className="col-md-4">
								<div className="p-3 h-100" style={{ background: 'var(--dark-surface-2, #f8fafc)', borderRadius: '10px' }}>
									<div style={mutedStyle}>{t('admin_partner_bonus_accrual_subscription_with_agents')}</div>
									<div style={{ ...moneyCellStyle, fontSize: '18px' }}>
										{formatMoney(comparison.subscription_with_agents || 0)}
									</div>
								</div>
							</div>
							<div className="col-md-4">
								<div className="p-3 h-100" style={{ background: 'var(--dark-surface-2, #f8fafc)', borderRadius: '10px' }}>
									<div style={mutedStyle}>{t('admin_partner_bonus_accrual_subscription_without_agents')}</div>
									<div style={{ ...moneyCellStyle, fontSize: '18px' }}>
										{formatMoney(comparison.subscription_without_agents || 0)}
									</div>
								</div>
							</div>
							<div className="col-md-4">
								<div className="p-3 h-100" style={{ background: 'var(--dark-tint-primary, #eef2ff)', borderRadius: '10px' }}>
									<div style={mutedStyle}>{t('admin_partner_bonus_accrual_subscription_total')}</div>
									<div style={{ ...moneyCellStyle, fontSize: '18px' }}>
										{formatMoney(comparison.subscription_total || 0)}
									</div>
								</div>
							</div>
						</div>

						<div className="table-responsive">
							<table className="table align-middle mb-0" style={{ fontSize: '13px' }}>
								<thead>
									<tr style={{ border: 'none' }}>
										<th className="py-3 text-muted fw-semibold" style={{ border: 'none', boxShadow: 'none', background: 'var(--dark-surface, #fff)', fontSize: '13px' }}>{t('indicator')}</th>
										<th className="py-3 text-muted fw-semibold text-end" style={{ border: 'none', boxShadow: 'none', background: 'var(--dark-surface, #fff)', fontSize: '13px' }}>{t('admin_partner_bonus_accrual_with_agents')}</th>
										<th className="py-3 text-muted fw-semibold text-end" style={{ border: 'none', boxShadow: 'none', background: 'var(--dark-surface, #fff)', fontSize: '13px' }}>{t('admin_partner_bonus_accrual_without_agents')}</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td>{t('admin_partner_bonus_accrual_subscription_total')}</td>
										<td className="text-end" style={moneyCellStyle}>
											{formatMoney(comparison.subscription_total || 0)}
										</td>
										<td className="text-end" style={moneyCellStyle}>
											{formatMoney(comparison.subscription_total || 0)}
										</td>
									</tr>
									<tr>
										<td>{t('admin_partner_bonus_accrual_paid')}</td>
										<td className="text-end" style={{ ...moneyCellStyle, color: 'var(--dark-fg-danger, #d23434)' }}>
											{formatMoney(comparison.bonus_paid || 0)}
										</td>
										<td className="text-end" style={moneyCellStyle}>0</td>
									</tr>
									<tr style={{ background: 'var(--dark-surface-2, #f8fafc)' }}>
										<td style={{ fontWeight: 700 }}>{t('admin_partner_bonus_accrual_net_profit')}</td>
										<td className="text-end" style={{ ...moneyCellStyle, fontWeight: 700, color: 'var(--dark-fg-success, #1f9d57)' }}>
											{formatMoney(comparison.net_profit_with_agents || 0)}
										</td>
										<td className="text-end" style={{ ...moneyCellStyle, fontWeight: 700, color: 'var(--dark-fg-success, #1f9d57)' }}>
											{formatMoney(comparison.net_profit_without_agents || 0)}
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			}
		</div>
	)
}

export default PartnerBonusAccrualView
